import { Component, DestroyRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import { BdeoSelectOption } from '@bdeo/webcomponents';
import { Damage, DamageSeverity, DisplayConditions, SeverityLevelMap, Smartphoto } from '@bdeo-motor/smartphoto-entities';
import { FormControl, FormGroup } from '@angular/forms';
import { I18NEXT_SERVICE } from 'angular-i18next';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DamageEstimationCardField } from '../../../entities/types/damage-estimation-card-field.type';
import { TransactionService } from '../../../services/transaction.service';
import { SmartphotoService } from '../../../services/smartphoto.service';
import { SmartphotoDamageEstimationDrawerComponent } from './smartphoto-damage-estimation-drawer/smartphoto-damage-estimation-drawer.component';
import { SmartphotoDamageEstimationPriceDrawerComponent } from './smartphoto-damage-estimation-price-drawer/smartphoto-damage-estimation-price-drawer.component';
import { CompanyService } from '../../../services/company.service';

type DamageType = 'claims' | 'others' | 'deleted';

@Component({
  selector: 'app-smartphoto-damage-estimation-list',
  templateUrl: './smartphoto-damage-estimation-list.component.html',
  styleUrl: './smartphoto-damage-estimation-list.component.scss'
})
export class SmartphotoDamageEstimationListComponent implements OnInit {
  private readonly i18NextService = inject(I18NEXT_SERVICE);
  protected readonly transactionService = inject(TransactionService);
  private readonly smartphotoService = inject(SmartphotoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly companyService = inject(CompanyService);
  public smartphoto?: Smartphoto;
  @Input() customPartsSelector?: string;
  @ViewChild(SmartphotoDamageEstimationDrawerComponent) addDamageDrawer!: SmartphotoDamageEstimationDrawerComponent;
  @ViewChild(SmartphotoDamageEstimationPriceDrawerComponent) priceDrawer!: SmartphotoDamageEstimationPriceDrawerComponent;
  public selectedDamages: Damage[] = [];
  public selectedOption: DamageType = 'claims';
  public selectedDamagesFields: DamageEstimationCardField[] = [];
  public labourPriceFields!: DamageEstimationCardField;
  public selectForm = new FormGroup({
    selector: new FormControl<string>('')
  });
  public selectOptions: BdeoSelectOption[] = [];
  public drawerDamage?: Damage;
  public showAddButton = false;
  public canAddNewDamage$ = this.smartphotoService.abilities.canAddNewDamage$;

  public ngOnInit(): void {
    this.smartphotoService
      .getCurrentSmartphoto()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((smartphoto) => {
        if (!smartphoto) {
          return;
        }

        this.smartphoto = smartphoto;
        this.selectForm.controls.selector.setValue('');
        this.updateDamageList();
      });
  }

  private checkAddButtonVisibility(): void {
    if (!this.smartphoto) {
      return;
    }

    const company = this.companyService.getCurrentCompany();

    if (!company) {
      return;
    }

    this.showAddButton = (DisplayConditions.canShowEditDamages(this.smartphoto, company) && this.selectedOption === 'claims') ?? false;
  }

  private setSelectorOptions(): void {
    if (!this.smartphoto) {
      return;
    }

    this.selectOptions = [];

    this.selectOptions.push({
      disabled: false,
      selected: true,
      value: this.smartphoto.reports.findMainReport().uuid,
      text: this.smartphotoService.getReportName(this.smartphoto.grouping, true, 0)
    });

    this.smartphoto.reports.findOtherReport().forEach((report, index) => {
      this.selectOptions.push({
        disabled: false,
        selected: false,
        value: report.uuid,
        text: this.smartphotoService.getReportName(this.smartphoto?.grouping ?? '', false, index)
      });
    });

    const deletedReport = this.smartphoto.reports.findDeleteReport();
    if (deletedReport.damages.length) {
      this.selectOptions.push({
        disabled: false,
        selected: false,
        value: deletedReport.uuid,
        text: this.i18NextService.t('brokers:estimation.deleted_damages')
      });
    }
  }

  public updateDamageList(): void {
    if (!this.smartphoto) {
      return;
    }

    const selectorValue = this.selectForm.get('selector')?.value;
    if (!selectorValue) {
      this.selectForm.get('selector')?.setValue(this.smartphoto.reports.findMainReport().uuid);
      this.selectedDamages = [...this.smartphoto.getMainDamages()];
      this.selectedOption = 'claims';
    } else if (selectorValue === this.smartphoto.reports.findMainReport().uuid) {
      this.selectedDamages = [...this.smartphoto.getMainDamages()];
      this.selectedOption = 'claims';
    } else if (selectorValue === this.smartphoto.reports.findDeleteReport().uuid) {
      this.selectedDamages = [...this.smartphoto.getDeletedDamages()];
      this.selectedOption = 'deleted';
    } else {
      this.selectedDamages = this.smartphoto.reports.findOtherReport().find((report) => report.uuid === selectorValue)?.damages ?? [];
      this.selectedOption = 'others';
    }

    this.setCardFields();
    this.setSelectorOptions();
    this.checkAddButtonVisibility();
  }

  private setCardFields(): void {
    if (!this.smartphoto?.estimation) {
      return;
    }

    const currency = this.smartphoto.estimationConfig.currency ?? '€';

    this.labourPriceFields = {
      title: this.i18NextService.t('brokers:estimation.bodyshop_time_price'),
      editable: true,
      fields: [
        {
          label: this.i18NextService.t('brokers:estimation.paint_time_price'),
          value: (this.smartphoto.estimationConfig.paintHourPrice ?? 0).toString(),
          dataType: 'number',
          unit: `${currency}/h`
        },
        {
          label: this.i18NextService.t('brokers:estimation.labour_price'),
          value: (this.smartphoto.estimationConfig.labourHourPrice ?? 0).toString(),
          dataType: 'number',
          unit: `${currency}/h`
        }
      ]
    };

    const severityColorMap: Record<DamageSeverity, string> = {
      high: 'var(--red-velvet)',
      mid: 'var(--old-sun)',
      low: 'var(--mango-mojito)',
      superficial: 'var(--pigeon-grey)',
      '-': 'black'
    };

    this.selectedDamagesFields = this.selectedDamages.map((damage) => {
      const fields = [
        {
          label: this.i18NextService.t('brokers:estimation.estimation_parts_damage_type'),
          value: this.i18NextService.t(`brokers:typologies.${damage.typology}`),
          severity: {
            label: this.i18NextService.t(`brokers:gravity.${damage.severity}`),
            iconName: `${SeverityLevelMap[damage.severity] ?? SeverityLevelMap.low}-gravity`,
            iconColor: severityColorMap[damage.severity]
          }
        },
        {
          label: this.i18NextService.t('brokers:estimation.estimation_parts_area'),
          value: damage.area.toString(),
          dataType: <const>'number',
          unit: 'dm²'
        },
        {
          label: this.i18NextService.t('brokers:estimation.estimation_parts_operation'),
          value: this.i18NextService.t(`brokers:operation.${damage.estimation.operation}`)
        }
      ];

      if (damage.estimation.operation === 'replace') {
        fields.push(
          {
            label: this.i18NextService.t('brokers:estimation.estimation_part_id'),
            value: damage.estimation.reference ?? '-'
          },
          {
            label: this.i18NextService.t('brokers:estimation.estimation_part_price'),
            value: (damage.estimation.piece ?? 0).toString(),
            dataType: <const>'number',
            unit: currency
          }
        );
      }
      fields.push(
        {
          label: this.i18NextService.t('brokers:estimation.estimation_parts_paint'),
          value: ((damage.estimation.paint.materials ?? 0) + (damage.estimation.paint.cost ?? 0)).toString(),
          dataType: <const>'number',
          unit: currency
        },
        {
          label: this.i18NextService.t('brokers:estimation.estimation_parts_labour'),
          value: ((damage.estimation.labour.cost ?? 0) + (damage.estimation.assembly.cost ?? 0)).toString(),
          dataType: <const>'number',
          unit: currency
        },
        {
          label: this.i18NextService.t('brokers:estimation.estimation_parts_total'),
          value: damage.estimation.total?.toString() ?? '0',
          dataType: <const>'number',
          unit: currency
        }
      );

      return {
        title: this.i18NextService.exists(`brokers:parts.${damage.description}`)
          ? this.i18NextService.t(`brokers:parts.${damage.description}`)
          : damage.description,
        uuid: damage.uuid,
        editable: !damage.deleted,
        hasActions: !damage.deleted,
        fields
      };
    });
  }

  public async handleDamageAdded(damage: Damage): Promise<void> {
    if (!this.smartphoto) {
      return;
    }

    if (!this.drawerDamage) {
      this.smartphoto.reports.addDamage(damage);
      this.addDamageDrawer.damage = undefined;
    } else {
      this.drawerDamage = undefined;
    }

    this.smartphoto.estimation?.calculate([...this.smartphoto.getMainDamages(), ...this.smartphoto.concepts]);
    this.smartphotoService.updateSmartphoto(this.smartphoto);
    this.smartphotoService.createUpdateTransaction();
  }

  public handleEditButtonClicked(damageUuid: string): void {
    this.drawerDamage = this.selectedDamages.find((d) => d.uuid === damageUuid);
    this.addDamageDrawer.updateParts();
    this.addDamageDrawer.open();
  }

  public handleEditPriceButtonClicked(): void {
    this.priceDrawer.open();
  }

  public handleActionButtonClicked(damageUuid: string): void {
    if (!this.smartphoto) {
      return;
    }

    const damage = this.selectedDamages.find((d) => d.uuid === damageUuid);
    this.smartphotoService.selectDamageForActions(damage);
  }

  public getAlreadyAddedParts(): string[] {
    if (!this.smartphoto) {
      return [];
    }

    return [...this.smartphoto.getAllDamages(), ...this.smartphoto.concepts, ...this.smartphoto.getDeletedDamages()]
      .flatMap(({ description }) => description)
      .filter(Boolean);
  }

  public add(): void {
    this.drawerDamage = undefined;
    this.addDamageDrawer.updateParts();
    this.addDamageDrawer.open();
  }
}
